import { fetchJSON as fetchAPI } from './client';

export type TimeHorizon = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'half_yearly' | 'yearly' | 'rolling';
export type PlanStatus = 'draft' | 'review' | 'submitted' | 'approved' | 'locked' | 'published' | 'archived';
export type VersionStatus = 'baseline' | 'working' | 'submitted' | 'approved' | 'locked' | 'archived' | 'historical';
export type ScenarioType = 'baseline' | 'optimistic' | 'conservative' | 'worst_case' | 'best_case' | 'custom';
export type HierarchyLevel = 'enterprise' | 'business_unit' | 'marketplace' | 'category' | 'brand' | 'product' | 'variant' | 'sku';

export type DemandPlan = {
  id: string;
  tenant_id: string;
  planning_ref_id?: string;
  name: string;
  description?: string;
  horizon: TimeHorizon;
  fiscal_year: number;
  status: PlanStatus;
  business_unit?: string;
  marketplace?: string;
  channel?: string;
  version_count: number;
  active_version_id?: string;
  created_at: string;
  updated_at: string;
};

export type DemandVersion = {
  id: string;
  tenant_id: string;
  plan_id: string;
  name: string;
  version: number;
  status: VersionStatus;
  is_baseline: boolean;
  locked: boolean;
  created_at: string;
  updated_at: string;
};

export type DemandScenario = {
  id: string;
  tenant_id: string;
  plan_id: string;
  version_id?: string;
  name: string;
  type: ScenarioType;
  description?: string;
  created_at: string;
};

export type DemandBucket = {
  id: string;
  version_id: string;
  period_label: string;
  level: HierarchyLevel;
  node_id: string;
  node_label: string;
  parent_node_id?: string;
  forecast_units: number;
  forecast_revenue: number;
};

export type HierarchyNode = {
  id: string;
  level: HierarchyLevel;
  label: string;
  parent_id?: string;
  children?: HierarchyNode[];
  forecast_units: number;
  forecast_revenue: number;
};

export type DemandAdjustment = {
  id: string;
  version_id: string;
  type: string;
  reason_code: string;
  description?: string;
  value: number;
  previous_units: number;
  new_units: number;
  applied_at: string;
};

export type DemandAssumption = {
  id: string;
  plan_id: string;
  version_id?: string;
  category: string;
  statement: string;
  value?: string;
  confidence: number;
  created_at: string;
};

export type DemandKPI = {
  id: string;
  plan_id: string;
  version_id?: string;
  forecast_units: number;
  forecast_revenue: number;
  growth_pct: number;
  variance_pct: number;
  forecast_accuracy_pct: number;
  bias_pct: number;
  top_opportunity?: string;
  top_risk?: string;
};

export type DemandApproval = {
  id: string;
  plan_id: string;
  version_id: string;
  status: string;
  requested_at: string;
};

export type DemandTask = {
  id: string;
  plan_id: string;
  title: string;
  status: string;
  due_date?: string;
};

export type DemandCalendar = {
  id: string;
  plan_id: string;
  name: string;
  horizon: TimeHorizon;
  fiscal_year: number;
  period_count: number;
};

export type DemandDashboard = {
  tenant_id: string;
  active_plans: number;
  working_versions: number;
  pending_approvals: number;
  open_tasks: number;
  active_scenarios: number;
  total_forecast_units: number;
  total_forecast_revenue: number;
  avg_growth_pct: number;
  variance_alerts: number;
  generated_at: string;
};

export type ScenarioComparison = {
  plan_id: string;
  scenarios: DemandScenario[];
  by_scenario: Record<string, DemandKPI>;
  variance_pct: Record<string, number>;
  generated_at: string;
};

export type VarianceReport = {
  plan_id: string;
  version_id: string;
  baseline_version_id: string;
  items: { node_label: string; level: HierarchyLevel; baseline_units: number; current_units: number; variance_pct: number }[];
  total_variance_pct: number;
  generated_at: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

const API_BASE = import.meta.env.VITE_DEMANDPLANNING_API ?? '/api/v1';

function demandFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const demandPlanningApi = {
  dashboard: () => demandFetch<DemandDashboard>('/demand-dashboard'),
  listPlans: () => demandFetch<DemandPlan[]>('/demand-plans'),
  getPlan: (id: string) => demandFetch<DemandPlan>(`/demand-plans/${id}`),
  submitPlan: (id: string) => demandFetch<DemandPlan>(`/demand-plans/${id}/submit`, { method: 'POST' }),
  approvePlan: (id: string) => demandFetch<DemandPlan>(`/demand-plans/${id}/approve`, { method: 'POST' }),
  lockPlan: (id: string) => demandFetch<DemandPlan>(`/demand-plans/${id}/lock`, { method: 'POST' }),
  listScenarios: (planId?: string) =>
    demandFetch<DemandScenario[]>(`/demand-scenarios${planId ? `?plan_id=${planId}` : ''}`),
  compareScenarios: (planId: string) =>
    demandFetch<ScenarioComparison>('/demand-scenarios/compare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan_id: planId }),
    }),
  listVersions: (planId?: string) =>
    demandFetch<DemandVersion[]>(`/demand-plans/versions${planId ? `?plan_id=${planId}` : ''}`),
  getHierarchy: (versionId: string) =>
    demandFetch<HierarchyNode[]>(`/demand-hierarchy?version_id=${versionId}`),
  getVariance: (planId: string, versionId?: string) =>
    demandFetch<VarianceReport>(`/demand-variance?plan_id=${planId}${versionId ? `&version_id=${versionId}` : ''}`),
  listKPIs: (planId?: string) =>
    demandFetch<DemandKPI[]>(`/demand-kpis${planId ? `?plan_id=${planId}` : ''}`),
  listAssumptions: (planId?: string) =>
    demandFetch<DemandAssumption[]>(`/demand-assumptions${planId ? `?plan_id=${planId}` : ''}`),
  listAdjustments: (versionId?: string) =>
    demandFetch<DemandAdjustment[]>(`/demand-adjustments${versionId ? `?version_id=${versionId}` : ''}`),
  listApprovals: () => demandFetch<DemandApproval[]>('/demand-approvals'),
  decideApproval: (id: string, approved: boolean) =>
    demandFetch<DemandApproval>(`/demand-approvals/${id}/decide`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved }),
    }),
  listTasks: (planId?: string) =>
    demandFetch<DemandTask[]>(`/demand-tasks${planId ? `?plan_id=${planId}` : ''}`),
  getCalendar: (planId?: string) =>
    demandFetch<DemandCalendar>(`/demand-calendar${planId ? `?plan_id=${planId}` : ''}`),
  listActivity: (limit = 20) => demandFetch<ActivityEntry[]>(`/demand-activity?limit=${limit}`),
};
